import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { Sun as SunIcon, Moon as MoonIcon, Home, Swords, Dumbbell, TrendingUp, Crown, Loader2, Puzzle as PuzzleIcon } from 'lucide-react';
import { TodayTab, type NavigableTab } from './components/today/TodayTab';
import { GameTab } from './components/game/GameTab';
import { ExercisesTab } from './components/exercises/ExercisesTab';
import { PuzzlesTab } from './components/puzzles/PuzzlesTab';
import { ProgressTab } from './components/progress/ProgressTab';
import { ChessComTab } from './components/chesscom/ChessComTab';
import { useLibrary } from './hooks/useLibrary';
import { useAnalysisQueue } from './hooks/useAnalysisQueue';
import { useChessComAutoSync } from './hooks/useChessComAutoSync';
import { analysisQueue } from './lib/coaching/analysisQueue';
import { isDue } from './lib/storage/review';
import { savePuzzleAttempt } from './lib/storage/db';
import type { PuzzleAttempt } from './types';

type Tab = 'aujourdhui' | 'partie' | 'exercices' | 'puzzles' | 'progression' | 'chesscom';

interface NavRequest {
  tab: Tab;
  openGameId?: string;
  puzzleThemes?: string[];
  nonce: number;
}

export default function App() {
  const [tab, setTab] = useState<Tab>('aujourdhui');
  const [navRequest, setNavRequest] = useState<NavRequest | null>(null);
  const library = useLibrary();
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useChessComAutoSync(library.refresh);
  useEffect(() => analysisQueue.onGameCompleted(() => library.refresh()), [library.refresh]);

  const queueState = useAnalysisQueue();
  const queueRemaining = queueState.pending.length + (queueState.current ? 1 : 0);
  // Suit la taille du plus gros lot en cours depuis la dernière file vide,
  // pour afficher « Analyse i/N » plutôt qu'un compte à rebours brut — se
  // remet à 0 dès que la file se vide (prêt pour le prochain lot).
  const [queueBatchTotal, setQueueBatchTotal] = useState(0);
  useEffect(() => {
    setQueueBatchTotal((prev) => (queueRemaining === 0 ? 0 : Math.max(prev, queueRemaining)));
  }, [queueRemaining]);
  const queueDone = queueBatchTotal - queueRemaining;

  const dueCount = library.exercises.filter((e) => isDue(e.review)).length;

  /** Navigation programmée (depuis Aujourd'hui, etc.) : change d'onglet et
   * porte des paramètres consommés une fois par l'onglet cible (ex. quelle
   * partie ouvrir). `nonce` force une nouvelle demande même vers le même id. */
  function navigate(target: NavigableTab, params?: { openGameId?: string; puzzleThemes?: string[] }) {
    setTab(target);
    setNavRequest({ tab: target, ...params, nonce: Date.now() });
  }

  async function handlePuzzleAttempt(attempt: PuzzleAttempt) {
    try {
      await savePuzzleAttempt(attempt);
      library.refresh();
    } catch {
      // La tentative reste comptée pour cette session même si l'enregistrement échoue.
    }
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <h1>
          <span className="app-logo" aria-hidden="true">
            ♞
          </span>{' '}
          Magnus
        </h1>
        <p className="tagline">Transforme ta dernière partie en 7 minutes d'apprentissage.</p>
      </header>

      <div className="tab-nav-row">
        <nav className="tab-nav" aria-label="Espaces Magnus">
          {/* aria-label explicite sur chaque bouton : le libellé texte est masqué en
              CSS sur mobile (icône seule), ce qui viderait sinon le nom accessible. */}
          <button
            type="button"
            className={tab === 'aujourdhui' ? 'active' : ''}
            onClick={() => setTab('aujourdhui')}
            aria-label="Aujourd'hui"
          >
            <Home className="h-4 w-4" aria-hidden="true" /> <span aria-hidden="true">Aujourd'hui</span>
          </button>
          <button
            type="button"
            className={tab === 'partie' ? 'active' : ''}
            onClick={() => setTab('partie')}
            aria-label="Ma partie"
          >
            <Swords className="h-4 w-4" aria-hidden="true" /> <span aria-hidden="true">Ma partie</span>
          </button>
          <button
            type="button"
            className={tab === 'chesscom' ? 'active' : ''}
            onClick={() => setTab('chesscom')}
            aria-label="Chess.com"
          >
            <Crown className="h-4 w-4" aria-hidden="true" /> <span aria-hidden="true">Chess.com</span>
          </button>
          <button
            type="button"
            className={tab === 'exercices' ? 'active' : ''}
            onClick={() => setTab('exercices')}
            aria-label={dueCount > 0 ? `Mes exercices, ${dueCount} dus` : 'Mes exercices'}
          >
            <Dumbbell className="h-4 w-4" aria-hidden="true" /> <span aria-hidden="true">Mes exercices</span>
            {dueCount > 0 && (
              <span className="due-badge" aria-hidden="true">
                {dueCount}
              </span>
            )}
          </button>
          <button
            type="button"
            className={tab === 'puzzles' ? 'active' : ''}
            onClick={() => setTab('puzzles')}
            aria-label="Puzzles"
          >
            <PuzzleIcon className="h-4 w-4" aria-hidden="true" /> <span aria-hidden="true">Puzzles</span>
          </button>
          <button
            type="button"
            className={tab === 'progression' ? 'active' : ''}
            onClick={() => setTab('progression')}
            aria-label="Ma progression"
          >
            <TrendingUp className="h-4 w-4" aria-hidden="true" /> <span aria-hidden="true">Ma progression</span>
          </button>
        </nav>
        {queueRemaining > 0 && (
          <span className="queue-status" role="status" aria-live="polite">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Analyse {Math.min(queueDone + 1, queueBatchTotal)}/{queueBatchTotal}
          </span>
        )}
        {mounted && (
          <button
            type="button"
            className="theme-toggle"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label="Changer de thème"
          >
            {resolvedTheme === 'dark' ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
          </button>
        )}
      </div>

      <main className="app-main">
        {tab === 'aujourdhui' && (
          <TodayTab
            games={library.games}
            exercises={library.exercises}
            reports={library.reports}
            puzzleAttempts={library.puzzleAttempts}
            onNavigate={navigate}
          />
        )}
        {tab === 'partie' && (
          <GameTab
            onLibraryChanged={library.refresh}
            games={library.games}
            reports={library.reports}
            openGameId={navRequest?.tab === 'partie' ? navRequest.openGameId : undefined}
            onGameOpened={() => setNavRequest(null)}
          />
        )}
        {tab === 'chesscom' && <ChessComTab onLibraryChanged={library.refresh} />}
        {tab === 'exercices' && (
          <ExercisesTab exercises={library.exercises} games={library.games} onChanged={library.refresh} />
        )}
        {tab === 'puzzles' && (
          <PuzzlesTab
            games={library.games}
            reports={library.reports}
            attempts={library.puzzleAttempts}
            onAttempt={handlePuzzleAttempt}
            requestedThemes={navRequest?.tab === 'puzzles' ? navRequest.puzzleThemes : undefined}
            onThemesConsumed={() => setNavRequest(null)}
          />
        )}
        {tab === 'progression' && (
          <ProgressTab
            games={library.games}
            reports={library.reports}
            exercises={library.exercises}
            attempts={library.attempts}
            puzzleAttempts={library.puzzleAttempts}
            storageError={library.storageError}
            onDataImported={library.refresh}
            onTrainWeakness={(themes) => navigate('puzzles', { puzzleThemes: themes })}
          />
        )}
      </main>

      <footer className="app-footer">
        <p className="muted small">
          Magnus — complément local à Chess.com, pas un outil d'assistance pendant la partie. Moteur : Stockfish
          (GPLv3). Aucune donnée n'est envoyée à un serveur.
        </p>
      </footer>
    </div>
  );
}
